import { useSyncExternalStore } from 'react';
import { z } from 'zod';

const STORAGE_KEY = 'phoebe-toast-paused-until';

/** One hour, matching the "Pause pop-ups for 1 hour" menu item. */
export const TOAST_PAUSE_DURATION_MS = 60 * 60 * 1000;

const pausedUntilSchema = z.number().int().positive();

// undefined: not yet loaded from storage. null: loaded, and confirmed not
// paused. A number: loaded, paused until that epoch ms.
let cachedPausedUntilMs: number | null | undefined;
const listeners = new Set<() => void>();

function readStoredPausedUntilMs(): number | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw === null) {
            return null;
        }
        const result = pausedUntilSchema.safeParse(Number(raw));
        return result.success ? result.data : null;
    } catch {
        // Private browsing and blocked storage both throw here. A pause is a
        // convenience, so degrade to "not paused" rather than failing.
        return null;
    }
}

function ensureCachedPausedUntilMs(): number | null {
    if (cachedPausedUntilMs === undefined) {
        cachedPausedUntilMs = readStoredPausedUntilMs();
    }
    return cachedPausedUntilMs;
}

function emitChange() {
    for (const listener of listeners) {
        listener();
    }
}

export const toastPauseStore = {
    getPausedUntilMs(): number | null {
        return ensureCachedPausedUntilMs();
    },
    isPaused(now: Date = new Date()): boolean {
        const pausedUntilMs = ensureCachedPausedUntilMs();
        return pausedUntilMs !== null && pausedUntilMs > now.getTime();
    },
    pauseForMs(durationMs: number) {
        const pausedUntilMs = Date.now() + durationMs;
        cachedPausedUntilMs = pausedUntilMs;
        try {
            localStorage.setItem(STORAGE_KEY, String(pausedUntilMs));
        } catch {
            // Keep the in-memory pause even when it cannot be persisted.
        }
        emitChange();
    },
    clear() {
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch {
            // Nothing to recover from; the cache is invalidated below.
        }
        // Invalidate rather than assume `null`: this forces the next read to
        // go back through storage, so a value written outside this store
        // (another tab, a test) is never masked by a stale cached "cleared".
        cachedPausedUntilMs = undefined;
        emitChange();
    },
    subscribe(listener: () => void): () => void {
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
        };
    },
};

export function useToastPaused(): boolean {
    return useSyncExternalStore(
        toastPauseStore.subscribe,
        () => toastPauseStore.isPaused(),
        () => false,
    );
}
